import { useMemo } from "react";

import type { ModelMapWidgetConfiguration } from "@/lib/api";
import { useAdminDashboard } from "@/features/admin/hooks";
import ModelMapWrapper from "@/features/shared/widgets/model-map/model-map";
import { WidgetContextHost } from "@/features/shared/widget-context/widget-context-host";
import { WidgetContextToolbar } from "@/features/shared/widget-context/widget-context-toolbar";
import { TimeRangeControl } from "@/features/shared/widget-context/controls/time-range-control";
import { RefreshControl } from "@/features/shared/widget-context/controls/refresh-control";
import previewStyles from "@/features/widget-forms/forms/map-widget-editor/widget-preview.module.css";

import { toPreviewConfiguration } from "./model-map-preview.utils";

type ModelMapPreviewProps = {
  /** Live draft config from the editor form (may be partial while editing). */
  configuration: Partial<ModelMapWidgetConfiguration>;
};

/**
 * CMS live preview of the model-map widget: mounts the real {@link ModelMapWrapper}
 * with the editor's live draft configuration inside a {@link WidgetContextHost}
 * (scoped time range, dashboard timezone, refresh takeover) and the shared map
 * preview size container. Fires real tile/series requests, same as the dashboard.
 *
 * Self-hosts the runtime for the inline preview column. When the preview needs a
 * header toolbar sharing the same runtime (the maximize dialog), the caller
 * instead wraps a {@link WidgetContextHost} around {@link ModelMapPreviewToolbar}
 * and {@link ModelMapPreviewContent}.
 */
export function ModelMapPreview({ configuration }: ModelMapPreviewProps) {
  const dashboard = useAdminDashboard(false);

  return (
    <WidgetContextHost dashboardId={dashboard?.id}>
      <ModelMapPreviewContent configuration={configuration} />
    </WidgetContextHost>
  );
}

/**
 * Time-range + refresh controls for the model-map preview. Must be mounted
 * within the same {@link WidgetContextHost} as {@link ModelMapPreviewContent}.
 */
export function ModelMapPreviewToolbar() {
  return (
    <WidgetContextToolbar>
      <TimeRangeControl />
      <RefreshControl />
    </WidgetContextToolbar>
  );
}

/**
 * Map body of the model-map preview (no runtime host of its own). The map state
 * machine only reads its layer input at init, so the widget is remounted when the
 * resolved model/layer/overlay set changes (base-layer-style and viewport edits
 * update in place).
 */
export function ModelMapPreviewContent({ configuration }: ModelMapPreviewProps) {
  const previewConfiguration = useMemo(
    () => toPreviewConfiguration(configuration),
    [
      configuration.model_id,
      configuration.parameter_map_layer_configurations,
      configuration.parameter_time_series_configurations,
      configuration.static_map_overlay_configurations,
      configuration.base_layer_style,
      configuration.initial_viewport_bounds,
    ],
  );

  const actorKey = useMemo(
    () =>
      JSON.stringify({
        model: previewConfiguration.model_id,
        layerIds: previewConfiguration.parameter_map_layer_configurations.map(
          (config) => config.parameter_map_layer_id,
        ),
        staticOverlayIds: previewConfiguration.static_map_overlay_configurations.map(
          (config) => config.static_map_overlay_id,
        ),
      }),
    [previewConfiguration],
  );

  return (
    <div className={previewStyles.host}>
      <ModelMapWrapper
        key={actorKey}
        configuration={previewConfiguration}
        containerHeight="100cqh"
      />
    </div>
  );
}
